import { isPlatformBrowser } from '@angular/common';
import {
  DOCUMENT,
  inject,
  Injectable,
  PLATFORM_ID,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { DataService } from '@core/services/data.service';
import { ReCaptchaService } from '@core/services/recaptcha.service';
import { ScriptService } from '@core/services/scripts.service';
import { TaggingService } from '@core/services/tagging.service';
import { TurnstileService } from '@core/services/turnstile.service';
import { environment } from '@environments/environment';
import { ENVIRONMENTS, RETAILERS } from '@shared/constants';
import { RETAILERS_CONFIG_MAP } from '@shared/constants/retailers-config-map';
import { Dict, Environment, ProjectType, ProjectTypes } from '@shared/models';

@Injectable({
  providedIn: 'root',
})
export class ConfigLoaderService {
  private readonly reCaptchaService: ReCaptchaService =
    inject(ReCaptchaService);
  private readonly turnstileService: TurnstileService =
    inject(TurnstileService);
  private readonly document: Document = inject(DOCUMENT);
  private readonly dataService: DataService = inject(DataService);
  private readonly scriptService: ScriptService = inject(ScriptService);
  private readonly taggingService: TaggingService = inject(TaggingService);
  private readonly rendererFactory: RendererFactory2 = inject(RendererFactory2);
  private readonly platformId: object = inject(PLATFORM_ID);

  private readonly renderer: Renderer2 = this.rendererFactory.createRenderer(
    null,
    null
  );

  /**
   * Load Configuration depends on environment
   *
   * @returns {void} Config as observable
   */
  loadConfig(): void {
    const env: Environment = this.getEnvironment();

    this.addPreconnectDomains(env); // it makes images load faster

    this.dataService.environment.set(env);
    const config = RETAILERS_CONFIG_MAP[env.retailerName];
    this.dataService.config.set(config);
    if (isPlatformBrowser(this.platformId)) {
      this.loadFonts(env.retailerName);
    }
    this.setFavIcon(`${env.baseHref}${config.appIcon}`);
    this.setThemeCssVariables(config.cssVariables);
    this.document.documentElement.lang = environment.lang.substring(0, 2);

    if (env.reCAPTCHAKey && env.production) {
      this.reCaptchaService.loadScript(env.reCAPTCHAKey);
    }

    if (env.cloudFlareTurnstileKey && env.production) {
      this.turnstileService.loadScript();
    }

    this.scriptService.load();
    this.taggingService.init();
  }

  private addPreconnectDomains(env: Environment): void {
    const linkElement: HTMLLinkElement = this.renderer.createElement('link');
    linkElement.rel = 'preconnect';
    linkElement.href = env.production
      ? 'https://logo.ai.eagleeye.com'
      : 'https://loremflickr.com';
    this.renderer.appendChild(this.document.head, linkElement);
  }

  /**
   * Load Fonts Dynamically
   * @param retailerName Retailer Name
   */
  private loadFonts(retailerName: RETAILERS): void {
    const linkElement: HTMLLinkElement = this.renderer.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = `${environment.baseHref}assets/fonts/${retailerName}/fonts.css`;
    this.renderer.appendChild(this.document.head, linkElement);
  }

  /**
   * Create Application favicon
   * @param iconUrl favicon URL
   */
  private setFavIcon(iconUrl: string): void {
    const linkElement: HTMLLinkElement = this.renderer.createElement('link');
    linkElement.rel = 'icon';
    linkElement.type = 'image/x-icon';
    linkElement.href = iconUrl;
    this.renderer.appendChild(this.document.head, linkElement);
  }

  /**
   * Set theme css root variables
   * @param variables Theme Variables
   */
  private setThemeCssVariables(variables: Dict): void {
    for (const [key, value] of Object.entries(variables)) {
      this.document.documentElement.style.setProperty(
        `--${key}`,
        String(value)
      );
    }
  }

  /**
   * Get Environment based on environment type
   *
   * @returns {Environment} Environment
   */
  private getEnvironment(): Environment {
    if (environment.env === ENVIRONMENTS.PLAYWRIGHT) {
      const urlParams = new URLSearchParams(this.document.location.search);

      const retailerName = (urlParams.get('retailer_name') ||
        RETAILERS.DEFAULT) as RETAILERS;
      const projectType = (urlParams.get('project_type') ||
        ProjectTypes.LOYALTY_CHALLENGE) as ProjectType;

      localStorage.setItem('retailerName', retailerName);
      localStorage.setItem('projectType', projectType);

      let hasCampaignBis = false;
      let hasBisPage = false;
      if ([RETAILERS.CHONDRUS, RETAILERS.CETOINE].includes(retailerName)) {
        hasCampaignBis = true;
      }
      if (retailerName === RETAILERS.CHONDRUS) {
        hasBisPage = true;
      }
      return {
        ...environment,
        projectType,
        retailerName,
        reCAPTCHAKey:
          retailerName === RETAILERS.CHONDRUS ? 'recaptcha-key' : '',
        hasCampaignBis: hasCampaignBis,
        hasBisPage: hasBisPage,
      };
    } else if (environment.env === ENVIRONMENTS.CYPRESS) {
      const retailerName = localStorage.getItem('retailerName') as RETAILERS;
      const projectType = localStorage.getItem('projectType') as ProjectType;

      let hasCampaignBis = false;
      let hasBisPage = false;

      if ([RETAILERS.CHONDRUS, RETAILERS.CETOINE].includes(retailerName)) {
        hasCampaignBis = true;
      }

      if (retailerName === RETAILERS.CHONDRUS) {
        hasBisPage = true;
      }

      return {
        ...environment,
        projectType,
        retailerName,
        reCAPTCHAKey:
          retailerName === RETAILERS.CHONDRUS ? 'recaptcha-key' : '',
        hasCampaignBis: hasCampaignBis,
        hasBisPage: hasBisPage,
      };
    }

    return environment;
  }
}
